import * as React from "react"
import { cn } from "../../lib/utils"
import { Input } from "./input"

export interface InputWithIconProps extends React.InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  label?: string
  description?: string
  error?: string
}

const InputWithIcon = React.forwardRef<HTMLInputElement, InputWithIconProps>(
  ({ 
    className, 
    leftIcon, 
    rightIcon, 
    label, 
    description, 
    error, 
    id,
    ...props 
  }, ref) => {
    const inputId = id || React.useId()

    const inputElement = (
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10">
            {leftIcon}
          </div>
        )}
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10">
            {rightIcon}
          </div>
        )}
        <Input
          id={inputId}
          className={cn(
            leftIcon && "pl-10",
            rightIcon && "pr-10",
            className
          )}
          ref={ref}
          aria-describedby={description ? `${inputId}-description` : undefined}
          aria-invalid={error ? true : undefined}
          {...props}
        />
      </div>
    )

    if (label || description || error) {
      return (
        <div className="space-y-2">
          {label && (
            <label htmlFor={inputId} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-foreground">
              {label}
            </label>
          )}
          {inputElement}
          {description && (
            <p id={`${inputId}-description`} className="text-sm text-muted-foreground">
              {description}
            </p>
          )}
          {error && (
            <p className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
      )
    }

    return inputElement
  }
)
InputWithIcon.displayName = "InputWithIcon"

export { InputWithIcon } 